import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { useErrorOnNotOkFetchye, useOneDataFetchye } from '@americanexpress/fetchye-amex';
import { useMembershipDomainFetchye } from '@americanexpress/consumer-travel-base';
import { MODULE_NAME } from '../constants/commonConstants';
import { MEMBERSHIP_DOMAIN_QUERY } from '../graphql/membershipQuery';
import {
  getNavigationOneCmsUrlSelector,
  getPersonaApiUrl,
} from '../selectors/apiSelectors';
import { NavigationLinks } from '../types/NavigationLinks';
import {
  fetchyeOpts,
  generateReporting,
  getPersonaAccounts,
  personaHeaders,
} from '../utils/apiUtils';
import { convertNavigationLinksToArrays } from '../utils/convertNavigationLinksToArray';
import { HTTP_METHODS, MEMBERSHIP_DOMAIN_CLIENT_ID } from '../constants/apiConstants';
import { getBenefitsFromPersona } from '../utils/personaUtils';

export const useIsLoggedIn = () => {
  const { data, isLoading, error } = useOneDataFetchye('ReadUserSession.v1', { method: 'GET' });
  return {
    data: !!data?.ok,
    isLoading,
    error,
  };
};

export const useMembershipApi = () => {
  const { data: isLoggedIn } = useIsLoggedIn();
  const activeLocale: string = useSelector(getActiveLocaleSelector);

  const fetcherOpts = {
    reporting: {
      moduleID: MODULE_NAME,
      component: 'useMembershipApi',
    },
    locale: activeLocale,
  };

  const {
    data,
    isLoading,
    error,
  } = useMembershipDomainFetchye(
    { query: MEMBERSHIP_DOMAIN_QUERY },
    {
      defer: !isLoggedIn,
      headers: { 'X-Client-Id': MEMBERSHIP_DOMAIN_CLIENT_ID },
    },
    fetcherOpts
  );

  return { data, isLoading, error };
};

export const useNavigationLinksHook = () => {
  const navigationLinksOneCmsUrl = useSelector(getNavigationOneCmsUrlSelector);

  const {
    isLoading,
    data,
    error,
  } = useErrorOnNotOkFetchye<NavigationLinks>(
    `${navigationLinksOneCmsUrl}`,
    fetchyeOpts,
    generateReporting(
      'src/apiHooks/useNavigationLinksHook'
    )
  );

  return {
    isLoading,
    data: data?.body ? convertNavigationLinksToArrays(data?.body) : undefined,
    error,
  };
};

export const usePersonaApi = (invokeBenefits : boolean) => {
  const locale = useSelector(getActiveLocaleSelector) as string;
  const {
    data: membershipApiData,
  } = useMembershipApi();
  const personaApiUrl = useSelector(getPersonaApiUrl);

  const cards = getPersonaAccounts(membershipApiData?.data);
  const defer = !(invokeBenefits && !!cards);

  const {
    data,
  } = useErrorOnNotOkFetchye(
    `${personaApiUrl}`,
    {
      ...personaHeaders(locale),
      method: HTTP_METHODS.POST,
      credentials: 'include',
      defer,
      body: JSON.stringify({
        cards,
      }),
    },
    generateReporting(
      'src/apiHooks/usePersonaApi'
    )
  );

  return {
    data: data?.body ? getBenefitsFromPersona(data?.body) : undefined,
  };
};
